import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { useMemo, useState } from "react";
import ReportingExportButton from "../../../components/Button/ReportingExportButton";
import AuditLogModal from "../../../components/Modal/AuditLogModal";
import IncidentPDFTemplate from "../../../components/PDF/Reporting/incident-report/IncidentPDFTemplate";
import QueryRetry from "../../../components/QueryRetry";
import ReportItemsPanel from "../../../components/Reporting/items/ReportItemsPanel";
import ReportCreatedLine from "../../../components/Reporting/ReportCreatedLine";
import {
	getAssetCard,
	getProjectFacts,
	getReviewerFact,
	getTenantFact,
	LookedUpValue,
} from "../../../components/Reporting/reportFacts";
import ReportHeader, {
	ReportHeaderFact,
} from "../../../components/Reporting/ReportHeader";
import {
	ReportConsequences,
	ReportPotentialRisks,
} from "../../../components/Reporting/ReportRisks";
import { useGlobalLoading } from "../../../hooks/useGlobalLoading";
import { useLookup } from "../../../hooks/useLookup";
import useReportDocumentsDrawer from "../../../hooks/useReportDocumentsDrawer";
import useReportEditor from "../../../hooks/useReportEditor";
import useReportItemNames from "../../../hooks/useReportItemNames";
import { getIncidentReportList } from "../../../services/api-server/incident_report";
import { getMtoCategoriesList } from "../../../services/api-server/mto_categories";
import { GetAntIcon } from "../../../utils/ant_icons";
import { formatDateTime } from "../../../utils/date_formatter";
import {
	addCauseCategoryNames,
	getCompanyNames,
	getIncidentExportData,
	getIncidentTypeLabel,
	showsIncidentConsequences,
} from "../../../utils/reporting/incidentReport";
import { createIncidentLookupConfig } from "../../../utils/reporting/lookupConfigs/incident";
import { getReportAuthorEmail } from "../../../utils/reporting/reportAuthor";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import { getReportStatusDisplay } from "../../../utils/reporting/statusDisplay";
import { handleExportReport } from "../../../utils/reports";

const ACTIONS = REPORT_ITEM_KINDS.follow_up_actions;

function IncidentReportOverview({ reportId }: Readonly<{ reportId: string }>) {
	const [auditLogOpen, setAuditLogOpen] = useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["report-overview", reportId],
		queryFn: async () => {
			const response: any = await getIncidentReportList({ _id: reportId });
			return response[0] ?? null;
		},
		enabled: !!reportId,
	});

	const editor = useReportEditor("incident_report", data, [
		"report-overview",
		reportId,
	]);
	const documents = useReportDocumentsDrawer("incident", reportId, data);

	useGlobalLoading("incident-report-overview", isLoading);

	const isRejected = data?.is_rejected === true;
	const statusDisplay = getReportStatusDisplay(
		data?.status,
		data?.open_status,
		isRejected
	);

	// lookup config need to be memoized because config is a dependency of the useLookup hook
	// to prevent new array reference being created on every render
	const lookupConfig = useMemo(
		() => createIncidentLookupConfig([data?.operator]),
		[data?.operator]
	);

	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		["lookup", "report-overview", reportId],
		data ? [data] : [],
		lookupConfig,
		0
	);
	const lookupData = dataWithLookup[0];

	// The tenant's cause categories, for the names in the PDF's causes table.
	const { data: mtoCategories = [], isLoading: isLoadingCategories } = useQuery(
		{
			queryKey: ["mto-categories", data?.operator],
			queryFn: async () => {
				if (!data?.operator) return [];
				return getMtoCategoriesList(data.operator);
			},
			enabled: !!data?.operator,
			staleTime: 5 * 60 * 1000,
		}
	);

	// The looked-up report with each cause's category names.
	const reportWithCauses = useMemo(
		() => addCauseCategoryNames(lookupData ?? data, mtoCategories),
		[data, lookupData, mtoCategories]
	);

	// From the report with its looked-up names and cause categories, so the
	// export keeps everything the PDF prints.
	const { emailToName, reportWithNames, isLoadingNames } = useReportItemNames(
		["report-actions-responsible", reportId],
		reportWithCauses,
		ACTIONS
	);

	if (isError) {
		// The drawers stay, so a failed background refetch doesn't close them.
		return (
			<>
				<QueryRetry queryKey={["report-overview", reportId]} />
				{documents.drawer}
				{editor.drawer}
			</>
		);
	}

	// The report form doesn't ask a condition's date and time.
	const dateFacts: ReportHeaderFact[] =
		data?.incident_type === "condition"
			? []
			: [
					{
						key: "date_time",
						label: "Date and time of incident",
						value: formatDateTime(data?.date_time ?? ""),
					},
				];

	const facts: ReportHeaderFact[] = [
		...dateFacts,
		{
			key: "department",
			label: "Department",
			value: (
				<LookedUpValue
					value={lookupData?.department_name}
					isLoading={isLoadingLookup}
				/>
			),
		},
		{
			key: "incident_type",
			label: "Incident type",
			value: getIncidentTypeLabel(data?.incident_type),
		},
		{
			key: "companies",
			label: "Companies involved",
			value: (
				<LookedUpValue
					value={getCompanyNames(lookupData?.companies_names)}
					isLoading={isLoadingLookup}
				/>
			),
		},
		...getProjectFacts(lookupData, isLoadingLookup),
		getReviewerFact(lookupData, isLoadingLookup),
		getTenantFact(data?.operator),
	];

	const created = (
		<ReportCreatedLine
			submittedOn={data?.submitted_on}
			authorName={lookupData?.author_name}
			authorEmail={getReportAuthorEmail(data)}
			historyTitle="Incident report review history"
			historyData={lookupData ?? data}
			onOpenAuditLog={() => setAuditLogOpen(true)}
		/>
	);

	return (
		<>
			{data && (
				// Keyed by report, like the actions panel: another incident opened
				// in place starts expanded, with its description clamped.
				<ReportHeader
					key={reportId}
					label="Incident report details"
					reportNumber={data.report_number}
					title={data.title}
					status={statusDisplay}
					actions={
						<>
							<Button icon={GetAntIcon("file2")} onClick={documents.open}>
								Additional documents
							</Button>
							<ReportingExportButton
								type="default"
								onExport={() =>
									handleExportReport(
										getIncidentExportData(reportWithNames),
										"incident",
										IncidentPDFTemplate
									)
								}
								isLoading={
									isLoadingLookup || isLoadingNames || isLoadingCategories
								}
							/>
						</>
					}
					created={created}
					facts={facts}
					media={getAssetCard(lookupData, isLoadingLookup)}
					summaries={[
						{
							key: "description",
							label: "Incident description",
							text: data.description,
						},
					]}
					sections={
						<>
							{showsIncidentConsequences(data) && (
								<ReportConsequences
									primary={data.primary_consequence}
									additional={data.consequences}
								/>
							)}
							<ReportPotentialRisks
								risks={data.potential_risks}
								operator={data.operator}
							/>
						</>
					}
				>
					{/* Keyed by report, so another incident opened in place starts
					    with its own filters and open rows. */}
					<ReportItemsPanel
						key={reportId}
						kind={ACTIONS}
						items={reportWithNames?.follow_up_actions ?? []}
						emailToName={emailToName}
						report={data}
						canEditItem={editor.canEditItem}
						onEditItem={editor.openItem}
						onAddItems={editor.canEdit ? editor.open : undefined}
					/>
				</ReportHeader>
			)}
			{/* Outside the header, so collapsing it never closes the log. */}
			{data && (
				<AuditLogModal
					auditlogFilter={{ level: "incident_report", level_id: data._id }}
					label="Incident report"
					type="reporting"
					open={auditLogOpen}
					handleClose={() => {
						setAuditLogOpen(false);
					}}
				/>
			)}
			{documents.drawer}
			{editor.drawer}
		</>
	);
}

export default IncidentReportOverview;
